import "@/app/style/videoPlayerControl.scss";
import { Slider, Box, Grid, Typography } from '@mui/material';
import React, { forwardRef, useRef, useState } from 'react';
import { Replay, Pause, PlayArrow, VolumeOff, VolumeUp, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { VideoSource } from "@/app/utils/class/common/video";
import { useLanguage } from "@/app/utils/LanguageContext";

interface ControlProps {
 isMobile: boolean;
 sources: VideoSource[];
 isPlaying: boolean;
 ended: boolean;
 currentTime: number;
 duration: number;
 played: number;
 loaded: number;
 mute: boolean;
 volume: number;
 selectedSourceIdx: number;
 fullscreen:boolean;
 onPlayPause: (e: any) => void;
 onSeek: (value:any) => void;
 onSeekMouseUp: (value:any) => void;
 onToggleMute: () => void;
 onVolumeChangeHandler: (e:any, value:any) => void;
 onVolumeSeekUp: (e:any, value:any) => void;
 onChangeSource: (idx: number)=>void;
 onToggleFullscreen: ()=>void;
 showCurrentTooltip?: boolean;
 showHoveredTooltip?: boolean;
}

const Control = forwardRef<HTMLDivElement, ControlProps>((props, ref) => {
  const { state: lang } = useLanguage();
  const { t } = lang;
  const [showTooltip, setShowTooltip] = useState(false);
  const [cursorValue, setCursorValue] = useState(0);
  const [sliderRect, setSliderRect] = useState<DOMRect>();
  const sliderRef = useRef<HTMLDivElement>(null);
  const currentTooltipRef = useRef<HTMLDivElement>(null);
  const hoveredTooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (event: any) => {
    setShowTooltip(true);
    updateCursorValue(event);
    if (sliderRef.current)
      setSliderRect(sliderRef.current.getBoundingClientRect())
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleMouseMove = (event: any) => {
    updateCursorValue(event);
  };

  const updateCursorValue = (event: any) => {
    if (!sliderRef.current) return;
    const sliderWidth = sliderRect?.width || 0;
    const cursorPosition = event.pageX - (sliderRect?.left || 0);
    const cursorValue = (cursorPosition / sliderWidth) * 100;
    setCursorValue(cursorValue);
  };

  const formatTime = (time: any) => {
    //formarting duration of video
    if (!time || !isFinite(time) || isNaN(time)) {
      return "00:00";
    }
  
    const date = new Date(time * 1000);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds().toString().padStart(2, "0");
    if (hours) {
      //if video have hours
      return `${hours}:${minutes.toString().padStart(2, "0")} `;
    } else return `${minutes}:${seconds}`;
  };

  const getTooltipPosition = (tooltipRef: React.RefObject<HTMLDivElement>, value: number) => {
    const tooltipWidth = tooltipRef.current ? tooltipRef.current.getBoundingClientRect().width : 0;
    const sliderWidth = sliderRect?.width || 0;
    const tooltipMin = tooltipWidth / 2;
    const tooltipMax = sliderWidth - tooltipMin;
    const tooltipPos = (value / 100) * sliderWidth;
    switch(true){
      case (tooltipPos < tooltipMin):
        return ((tooltipPos/tooltipMin) * 50) * -1
      case (tooltipPos > tooltipMax):
        return (((tooltipPos - tooltipMax)/tooltipMin * 50) + 50) * -1
      default:
        return -50
    }
  };

  return(
    <Box className="controlContainer" id="videoControl" ref={ref}>
      <Box className="sliderBox"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <Slider className={`progressBar clickable${props.isMobile ? " mobile" : ""}`}
          ref={sliderRef}
          sx={{
            "& .MuiSlider-rail": {
              background: `linear-gradient(to right, white 0% ${props.loaded * 100}%, transparent ${props.loaded * 100}% 100%)`
            }
          }}
          min={0}
          max={100}
          value = {props.played * 100} 
          onClick={(e: any)=>{
            e.preventDefault();
          }}
          onChange={(e: any, value: any) => {
            e.preventDefault();
            props.onSeek(value)
          }}
          onChangeCommitted={(e: any, value: any) => {
            e.preventDefault();
            props.onSeekMouseUp(value)
          }}/>
          { !props.isMobile && showTooltip && (props.showCurrentTooltip || props.showHoveredTooltip) && (
            <>
            {
              props.showCurrentTooltip && (
                <div
                  className="indicator current"
                  style={{
                    left: `calc(${props.played * 100}% - 1px)`,
                  }}>
                    <div
                    ref={currentTooltipRef}
                    className="tooltip white"
                    style={{
                      transform: `translateX(${getTooltipPosition(currentTooltipRef, props.played * 100)}%)`,
                    }}> {formatTime(props.currentTime)}</div>
                </div>
              )
            }
            {
              props.showHoveredTooltip && (
                <div
                  className="indicator hovered"
                  style={{
                    left: `calc(${cursorValue}% - 1px)`,
                  }}>
                    <div
                    ref={hoveredTooltipRef}
                    className="tooltip black"
                    style={{
                      transform: `translateX(${getTooltipPosition(hoveredTooltipRef, cursorValue)}%)`,
                    }}> {formatTime(cursorValue / 100 * props.duration)}</div>
                </div>
              )
            }
            </>
          )}
      </Box>
      <Grid className="controlBox" columns={12} container>
        <Grid item className="controlPanel left" columns={4}>
          <Grid item className="iconBtn clickable">
            <Box onClick={(e) => props.onPlayPause(e)} style={{display: "flex"}}>
              { props.ended ? (<Replay fontSize="medium" />) : 
                  props.isPlaying ? (<Pause fontSize="medium"/>) : (<PlayArrow fontSize="medium" />)
              }
            </Box>
          </Grid>
          <Box style={{alignSelf: "center"}}>
            <Typography className="videoTimestamp">{ formatTime(props.currentTime)} / {formatTime(props.duration)}</Typography>
          </Box>
        </Grid>
        <Grid item className="controlPanel right" columns={8}>
          <Grid item className="iconBtn volumeBtn clickable">
            <Box onClick={props.onToggleMute} style={{display: "flex"}}>
              { props.mute ? (<VolumeOff fontSize="medium" />) : (<VolumeUp fontSize="medium" />) }
            </Box>
          </Grid>
          {
            !props.isMobile && (<Box className="volumeSliderWrapper">
              <Slider
                  className={`volumeSlider clickable${props.isMobile ? '' : ' desktop'}`}
                  value={props.volume * 100}
                  onChange={(e, value) => props.onVolumeChangeHandler(e, value)}
                  onChangeCommitted={props.onVolumeSeekUp}
                />
            </Box>)
          }
          {
            !props.isMobile && props.sources && props.sources.length > 1 && props.sources.map((s: VideoSource, idx: number) => (
              <Grid item className={`qualityBtn clickable ${idx === props.selectedSourceIdx ? 'selected': 'hoverable'}`} onClick={() => props.onChangeSource(idx)} key={s.keyName}>
                <Box>{t(`video_quality_btn_${s.keyName}`)}</Box>
              </Grid>
            ))
          }
          <Grid item className="iconBtn clickable">
            <Box onClick={() => props.onToggleFullscreen()} style={{display: "flex"}}>
              { props.fullscreen ? (<FullscreenExit fontSize="medium" />) : (<Fullscreen fontSize="medium" />) }
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
    )
 });

Control.displayName = 'Control'

export default Control;